'use client';

import React, { useState, useRef, useEffect } from "react";
import { EditorBubbleItem, useEditor } from "novel";
import { Tooltip as ReactTippyTooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import {
  Type,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  List,
  ListOrdered,
  Quote,
  FileCode,
  CheckSquare,
  ChevronDown
} from 'lucide-react';

// Create a wrapper component for Tooltip to fix TypeScript issues
const Tooltip = ({ children, ...props }: { children: React.ReactNode } & any) => {
  return <ReactTippyTooltip {...props}>{children}</ReactTippyTooltip>;
};

export interface SelectorItem {
  name: string;
  icon: React.ReactNode;
  command: (editor: any) => void;
  isActive: (editor: any) => boolean;
}

interface NodeSelectorProps {
  open: boolean;
  hideText?: boolean;
  onOpenChange: (open: boolean) => void;
  editor?: any; // Allow editor to be passed as a prop
}

export const NodeSelector = ({ open, hideText, onOpenChange, editor: editorProp }: NodeSelectorProps) => {
  const { editor: editorFromContext } = useEditor();
  const editor = editorProp || editorFromContext;
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open, onOpenChange]);

  if (!editor) return null;

  const items: SelectorItem[] = [
    {
      name: "Text",
      icon: <Type className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().setParagraph().run(),
      isActive: (editor) => editor.isActive("paragraph") && !editor.isActive("bulletList") && !editor.isActive("orderedList"),
    },
    {
      name: "Heading 1",
      icon: <Heading1 className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: (editor) => editor.isActive("heading", { level: 1 }),
    },
    {
      name: "Heading 2",
      icon: <Heading2 className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: (editor) => editor.isActive("heading", { level: 2 }),
    },
    {
      name: "Heading 3",
      icon: <Heading3 className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: (editor) => editor.isActive("heading", { level: 3 }),
    },
    {
      name: "Heading 4",
      icon: <Heading4 className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
      isActive: (editor) => editor.isActive("heading", { level: 4 }),
    },
    {
      name: "Heading 5",
      icon: <Heading5 className="h-4 w-4" />,
      command: (editor) => editor.chain().focus().toggleHeading({ level: 5 }).run(),
      isActive: (editor) => editor.isActive("heading", { level: 5 }),
    }
  ];

  const activeItem = items.find((item) => item.isActive(editor));

  return (
    <div className="relative" ref={dropdownRef}>
      <Tooltip
        title={activeItem?.name || "Text"}
        position="bottom"
        animation="fade"
        arrow={true}
        delay={100}
        distance={10}
        theme="dark"
        hideOnClick={false}
        style={{ display: 'flex' }}
      >
        <button
          onClick={() => onOpenChange(!open)}
          className={`relative flex h-8 items-center gap-2 rounded-md px-2 hover:bg-stone-100 active:bg-stone-200 ${hideText ? "w-12 justify-center" : "w-32 pr-8"}`}
          aria-label="Text style"
        >
          <span className="flex items-center gap-2 min-w-0 flex-1 justify-start">
            <span className="flex-shrink-0">{activeItem?.icon}</span>
            {!hideText && <span className="text-sm font-medium truncate">{activeItem?.name || "Text"}</span>}
          </span>
          <ChevronDown className={`h-3 w-3 text-stone-500 flex-shrink-0 ${!hideText ? "absolute right-2 top-1/2 -translate-y-1/2" : ""}`} />
        </button>
      </Tooltip>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-md border border-stone-200 bg-white p-1 shadow-md">
          {items.map((item, index) => (
            <a
              key={index}
              onClick={() => {
                item.command(editor);
                onOpenChange(false);
              }}
              className={`flex w-full items-center rounded-md p-2 text-sm cursor-pointer hover:bg-stone-100 ${item.isActive(editor) ? "bg-stone-100" : ""
                }`}
            >
              <span className="w-6 h-6 flex items-center justify-center mr-2 flex-shrink-0">
                {item.icon}
              </span>
              <span>{item.name}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};
